import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Portal,
  Snackbar,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  Archive as ArchiveIcon,
  DeleteSweep as DeleteSweepIcon,
  PushPin as PushPinIcon,
  PendingActions as PendingActionsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ReportsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f5f5',
  minHeight: '100vh'
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StatsCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  '&.total': {
    borderLeft: `4px solid ${theme.palette.primary.main}`
  },
  '&.completed': {
    borderLeft: `4px solid ${theme.palette.success.main}`
  },
  '&.pending': {
    borderLeft: `4px solid ${theme.palette.warning.main}`
  }
}));

import apiService from '../services/api';

const formatRecommendation = (value, fallback = 'N/A') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  if (!text) return fallback;

  const parts = text.split(/[;,/\n]+/).map((part) => part.trim()).filter(Boolean);
  const first = parts[0] || text;
  const key = first.toLowerCase();

  if (
    key === 'repair' ||
    key === 'require repair' ||
    key === 'requires repair' ||
    /:\s*(?:requires?\s+)?repair$/.test(key)
  ) {
    return 'Require repair';
  }

  return first;
};

const Reports = ({ projectId, projectName, buildingId, imageType = 'drone', onBack, onNotificationUpsert }) => {
  const isDev = process.env.NODE_ENV === 'development';
  const canProxy = typeof window !== 'undefined' && (window.location.port === '3000' || window.location.port === '3001');
  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    ((isDev && canProxy)
      ? ''
      : (typeof window !== 'undefined'
          ? 'https://api-report.pxi.maxzrange.com'
          : 'https://api-report.pxi.maxzrange.com'));

  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [reportFormat, setReportFormat] = useState('PDF');
  const [reportType, setReportType] = useState('full');
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [realDefects, setRealDefects] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const previewImgRef = useRef(null);
  const closeReportTimerRef = useRef(null);
  const reportIndicatorHideTimerRef = useRef(null);
  const reportStartedAtRef = useRef(0);
  const reportOpenTimerRef = useRef(null);
  const reportJobPollTimerRef = useRef(null);
  const reportJobPollErrorCountRef = useRef(0);
  const reportNotificationIdRef = useRef(null);
  const reportUiStartedRef = useRef(false);
  const onNotificationUpsertRef = useRef(onNotificationUpsert);
  const isMountedRef = useRef(true);
  const [imgDims, setImgDims] = useState({ naturalWidth: 0, displayedWidth: 0 });
  const [projectStats, setProjectStats] = useState({
    totalImages: 0,
    annotatedImages: 0,
    totalElevations: 0,
    levelsPerElevation: '0'
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportJob, setReportJob] = useState(null);
  const [reportElapsedSeconds, setReportElapsedSeconds] = useState(0);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportIndicatorVisible, setReportIndicatorVisible] = useState(false);
  const [reportExpectedSeconds, setReportExpectedSeconds] = useState(12);
  const [reportReadyToOpen, setReportReadyToOpen] = useState(false);
  const [reportFileUrl, setReportFileUrl] = useState('');
  const [generatedReports, setGeneratedReports] = useState([]);
  const [reportListLoading, setReportListLoading] = useState(false);
  const [reportListError, setReportListError] = useState('');
  const [cleanupRules, setCleanupRules] = useState(null);
  const [updatingRetentionKey, setUpdatingRetentionKey] = useState('');
  const [reportListBuildingId, setReportListBuildingId] = useState(buildingId ? String(buildingId) : 'all');
  const [reportListFileType, setReportListFileType] = useState('all');
  const [reportListSearch, setReportListSearch] = useState('');
  const [selectedReportKeys, setSelectedReportKeys] = useState([]);
  const [deletingReports, setDeletingReports] = useState(false);
  const [showReportStatusDialog, setShowReportStatusDialog] = useState(false);
  const [reportJobStatusSummary, setReportJobStatusSummary] = useState(null);
  const [reportJobStatusLoading, setReportJobStatusLoading] = useState(false);
  const [reportJobStatusError, setReportJobStatusError] = useState('');
  const [reportPage, setReportPage] = useState(0);
  const [reportRowsPerPage, setReportRowsPerPage] = useState(10);
  const [defectSearch, setDefectSearch] = useState('');
  const [defectSeverityFilter, setDefectSeverityFilter] = useState('all');
  const [defectTypeFilter, setDefectTypeFilter] = useState('all');
  const [defectPage, setDefectPage] = useState(0);
  const [defectRowsPerPage, setDefectRowsPerPage] = useState(10);
  const [elevationCoverMap, setElevationCoverMap] = useState({});
  const [keyFindingImageId, setKeyFindingImageId] = useState(() => {
    try {
      const raw = localStorage.getItem(`key_finding_building_${buildingId || 'global'}`);
      return raw || '';
    } catch {
      return '';
    }
  });
  const [keyFindingImageIds, setKeyFindingImageIds] = useState(() => {
    try {
      const raw = localStorage.getItem(`key_findings_${imageType || 'drone'}_building_${buildingId || 'global'}`) || '[]';
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    onNotificationUpsertRef.current = onNotificationUpsert;
  }, [onNotificationUpsert]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (closeReportTimerRef.current) clearTimeout(closeReportTimerRef.current);
      if (reportIndicatorHideTimerRef.current) clearTimeout(reportIndicatorHideTimerRef.current);
      if (reportOpenTimerRef.current) clearTimeout(reportOpenTimerRef.current);
      if (reportJobPollTimerRef.current) clearInterval(reportJobPollTimerRef.current);
    };
  }, []);

  const clearReportIndicatorHideTimer = () => {
    if (reportIndicatorHideTimerRef.current) {
      clearTimeout(reportIndicatorHideTimerRef.current);
      reportIndicatorHideTimerRef.current = null;
    }
  };

  const formatReportDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatReportSize = (value) => {
    const size = Number(value || 0);
    if (!Number.isFinite(size) || size <= 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let nextSize = size;
    let unitIndex = 0;
    while (nextSize >= 1024 && unitIndex < units.length - 1) {
      nextSize /= 1024;
      unitIndex += 1;
    }
    return `${nextSize.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const formatJobDuration = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(seconds || 0)));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getJobTargetLabel = (job) => {
    if (!job) return '-';
    const targetId = job.target_id;
    if (job.job_type === 'project-report') {
      if (projectId && String(projectId) === String(targetId) && projectName) return projectName;
      return targetId ? `Project #${targetId}` : 'Project report';
    }
    const match = buildings.find((item) => String(item.id) === String(targetId));
    if (match?.name) return match.name;
    return targetId ? `Building #${targetId}` : 'Building report';
  };

  const getJobStatusColor = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'running') return 'primary';
    if (normalized === 'queued') return 'warning';
    if (normalized === 'completed') return 'success';
    if (normalized === 'failed') return 'error';
    return 'default';
  };

  const hideReportIndicator = () => {
    clearReportIndicatorHideTimer();
    if (isMountedRef.current) {
      setReportIndicatorVisible(false);
    }
  };

  const scheduleReportIndicatorHide = (minimumMs = 2500) => {
    const elapsedMs = reportStartedAtRef.current ? Date.now() - reportStartedAtRef.current : minimumMs;
    const remainingMs = Math.max(0, minimumMs - elapsedMs);
    clearReportIndicatorHideTimer();
    reportIndicatorHideTimerRef.current = window.setTimeout(() => {
      hideReportIndicator();
    }, remainingMs);
  };

  const pushReportNotification = (overrides = {}) => {
    const notify = onNotificationUpsertRef.current;
    if (typeof notify !== 'function') return null;

    const nextNotification = {
      id: reportNotificationIdRef.current || undefined,
      type: 'report',
      title: 'Generating report',
      message: 'Generating report...',
      status: 'running',
      progress: Math.min(100, Math.max(0, reportProgress || 0)),
      read: false,
      ...overrides,
    };

    const nextId = notify(nextNotification);
    if (!reportNotificationIdRef.current && nextId) {
      reportNotificationIdRef.current = nextId;
    }
    return nextId || reportNotificationIdRef.current;
  };

  const getEstimatedReportSeconds = () => {
    const imageCount = Number(projectStats.totalImages || 0);
    const defectCount = Number(realDefects.length || 0);
    const buildingFactor = selectedBuildingId === 'combined' ? 8 : 0;
    const normalizedFormat = String(reportFormat || '').toUpperCase();
    const formatFactor = normalizedFormat === 'DOCX' ? 24 : normalizedFormat === 'XLSX' ? 8 : 4;
    const base = 8 + formatFactor + buildingFactor;
    const imageDivisor = normalizedFormat === 'DOCX' ? 2 : 4;
    const defectDivisor = normalizedFormat === 'DOCX' ? 8 : 12;
    const sizeFactor = Math.ceil(imageCount / imageDivisor) + Math.ceil(defectCount / defectDivisor);
    const maxSeconds = normalizedFormat === 'DOCX' ? 300 : 90;
    return Math.max(12, Math.min(maxSeconds, Math.round((base + sizeFactor) * 1.4)));
  };

  const loadStoredKeyFinding = (buildingKey) => {
    const key = buildingKey || 'global';
    try {
      return localStorage.getItem(`key_finding_building_${key}`) || '';
    } catch {
      return '';
    }
  };

  const loadStoredDroneKeyFindings = (buildingKey) => {
    const key = buildingKey || 'global';
    try {
      const raw = localStorage.getItem(`key_findings_drone_building_${key}`) || '[]';
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const getBuildingDisplayName = () => {
    if (selectedBuildingId === 'combined') {
      return 'All Buildings';
    }
    const match = buildings.find((item) => String(item.id) === String(selectedBuildingId));
    if (match?.name) return match.name;
    if (buildingId && String(buildingId) === String(selectedBuildingId)) {
      return `Building #${buildingId}`;
    }
    return selectedBuildingId ? `Building #${selectedBuildingId}` : '';
  };

  const getReportFilename = (fileUrl) => {
    if (!fileUrl) return '';
    const cleaned = String(fileUrl).split('?')[0].split('#')[0];
    const parts = cleaned.split('/').filter(Boolean);
    return parts[parts.length - 1] || '';
  };

  const getReportName = (fileUrl) => {
    const filename = getReportFilename(fileUrl);
    if (!filename) return '';
    return filename.replace(/\.[^.]+$/, '');
  };

  const getReportNotificationDetails = (fileUrl = '') => ({
    projectName: getProjectDisplayName(),
    buildingName: getBuildingDisplayName(),
    format: String(reportFormat || '').toUpperCase(),
    filename: getReportFilename(fileUrl),
    reportName: getReportName(fileUrl),
    file_url: fileUrl || '',
  });

  const getProjectDisplayName = () => {
    if (projectName) return projectName;
    if (projectId) return `Project #${projectId}`;
    return '';
  };

  const getReportListScope = () => {
    if (reportListBuildingId === 'combined') {
      return {
        type: 'project',
        id: projectId,
        label: 'Combined Project Reports',
      };
    }
    if (reportListBuildingId === 'all' && projectId) {
      return {
        type: 'project-all',
        id: projectId,
        label: 'All Generated Reports',
      };
    }
    const targetBuildingId = reportListBuildingId || buildingId;
    return {
      type: 'building',
      id: targetBuildingId,
      label: targetBuildingId ? `Reports for ${getDefectBuildingName(targetBuildingId) || `Building #${targetBuildingId}`}` : 'Generated Reports',
    };
  };

  const handleSharedBuildingFilterChange = (value) => {
    setReportListBuildingId(value);
    setDefectSeverityFilter('all');
    setDefectTypeFilter('all');
    setReportPage(0);
    setDefectPage(0);
  };

  const loadGeneratedReports = async () => {
    const scope = getReportListScope();
    if (!scope.id) {
      setGeneratedReports([]);
      setCleanupRules(null);
      return;
    }
    try {
      setReportListLoading(true);
      setReportListError('');
      if (scope.type === 'project-all') {
        const [projectReportsResponse, buildingReportResponses] = await Promise.all([
          apiService.getProjectReports(scope.id).catch((error) => {
            console.error('Failed to load combined project reports:', error);
            return null;
          }),
          Promise.all((buildings || []).map(async (building) => {
            try {
              const response = await apiService.getBuildingReports(building.id);
              const reports = Array.isArray(response?.reports) ? response.reports : [];
              return reports.map((report) => ({
                ...report,
                scope_type: 'building',
                scope_id: building.id,
                scope_label: building.name || `Building #${building.id}`,
              }));
            } catch (error) {
              console.error(`Failed to load reports for building ${building.id}:`, error);
              return [];
            }
          })),
        ]);
        const projectReports = (Array.isArray(projectReportsResponse?.reports) ? projectReportsResponse.reports : []).map((report) => ({
          ...report,
          scope_type: 'project',
          scope_id: scope.id,
          scope_label: 'Combined Project',
        }));
        const buildingReports = buildingReportResponses.flat();
        const combinedReports = [...projectReports, ...buildingReports].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setGeneratedReports(combinedReports);
        setCleanupRules(projectReportsResponse?.cleanup_rules || null);
      } else {
        const response = scope.type === 'project'
          ? await apiService.getProjectReports(scope.id)
          : await apiService.getBuildingReports(scope.id);
        const reports = Array.isArray(response?.reports) ? response.reports : [];
        setGeneratedReports(reports.map((report) => ({
          ...report,
          scope_type: scope.type,
          scope_id: scope.id,
          scope_label: scope.label,
        })));
        setCleanupRules(response?.cleanup_rules || null);
      }
    } catch (error) {
      console.error('Failed to load generated reports:', error);
      setGeneratedReports([]);
      setCleanupRules(null);
      setReportListError('Failed to load generated reports.');
    } finally {
      setReportListLoading(false);
    }
  };

  const loadReportJobStatuses = async ({ silent = false } = {}) => {
    try {
      if (!silent) setReportJobStatusLoading(true);
      setReportJobStatusError('');
      const response = await apiService.getActiveReportJobs();
      const activeJobs = Array.isArray(response?.active_jobs)
        ? response.active_jobs
        : response?.active_job
          ? [response.active_job]
          : [];
      setReportJobStatusSummary({
        ...(response || {}),
        active_jobs: activeJobs,
      });
    } catch (error) {
      console.error('Failed to load report job statuses:', error);
      setReportJobStatusError('Failed to load report job status.');
    } finally {
      if (!silent) setReportJobStatusLoading(false);
    }
  };

  useEffect(() => {
    if (!showReportStatusDialog) return undefined;
    let cancelled = false;

    const refreshStatuses = async (options = {}) => {
      if (cancelled) return;
      await loadReportJobStatuses(options);
    };

    refreshStatuses();
    const timer = window.setInterval(() => {
      refreshStatuses({ silent: true });
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [showReportStatusDialog]);

  const handleToggleReportRetention = async (report) => {
    const scope = getReportListScope();
    const reportScopeType = report?.scope_type || scope.type;
    const reportScopeId = report?.scope_id || scope.id;
    if (!reportScopeId || !report?.filename) return;
    const nextRetained = !report.retained;
    const updateKey = `${reportScopeType}:${reportScopeId}:${report.filename}`;
    try {
      setUpdatingRetentionKey(updateKey);
      if (reportScopeType === 'project') {
        await apiService.updateProjectReportRetention(reportScopeId, report.filename, nextRetained);
      } else {
        await apiService.updateBuildingReportRetention(reportScopeId, report.filename, nextRetained);
      }
      await loadGeneratedReports();
    } catch (error) {
      console.error('Failed to update report retention:', error);
      setReportListError('Failed to update report retention.');
    } finally {
      setUpdatingRetentionKey('');
    }
  };

  const getReportSelectionKey = (report) => {
    const scope = getReportListScope();
    const reportScopeType = report?.scope_type || scope.type;
    const reportScopeId = report?.scope_id || scope.id;
    return `${reportScopeType}:${reportScopeId}:${report?.filename || ''}`;
  };

  const handleToggleReportSelection = (report) => {
    const key = getReportSelectionKey(report);
    setSelectedReportKeys((current) => (
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    ));
  };

  const handleToggleAllReports = (checked) => {
    const pageKeys = filteredGeneratedReports.map(getReportSelectionKey);
    setSelectedReportKeys((current) => {
      if (!checked) return current.filter((key) => !pageKeys.includes(key));
      return Array.from(new Set([...current, ...pageKeys]));
    });
  };

  const handleDeleteSelectedReports = async () => {
    const selectedReports = generatedReports.filter((report) => selectedReportKeys.includes(getReportSelectionKey(report)));
    if (selectedReports.length === 0) return;
    const confirmed = window.confirm(`Delete ${selectedReports.length} selected report${selectedReports.length === 1 ? '' : 's'}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeletingReports(true);
      setReportListError('');
      const scope = getReportListScope();
      await Promise.all(selectedReports.map((report) => {
        const reportScopeType = report.scope_type || scope.type;
        const reportScopeId = report.scope_id || scope.id;
        if (!reportScopeId || !report.filename) return Promise.resolve();
        if (reportScopeType === 'project') {
          return apiService.deleteProjectReport(reportScopeId, report.filename);
        }
        return apiService.deleteBuildingReport(reportScopeId, report.filename);
      }));
      setSelectedReportKeys([]);
      await loadGeneratedReports();
    } catch (error) {
      console.error('Failed to delete selected reports:', error);
      setReportListError('Failed to delete selected reports.');
    } finally {
      setDeletingReports(false);
    }
  };

  const filteredGeneratedReports = React.useMemo(() => {
    const query = reportListSearch.trim().toLowerCase();
    return generatedReports.filter((report) => [
      (() => {
        if (reportListFileType === 'all') return true;
        const filename = String(report.filename || report.file_url || '').split('?')[0].split('#')[0].toLowerCase();
        return filename.endsWith(`.${reportListFileType}`);
      })(),
      !query || [
      report.filename,
      report.file_url,
      report.scope_label,
      report.created_at,
      report.generated_by_name,
      report.cleanup_reason,
      report.retained ? 'keep protected retained' : 'not kept unmarked',
      report.cleanup_candidate ? 'cleanup candidate' : '',
      ].some((value) => String(value || '').toLowerCase().includes(query)),
    ].every(Boolean));
  }, [generatedReports, reportListSearch, reportListFileType]);

  const reportFileTypeOptions = React.useMemo(() => {
    const types = new Set();
    generatedReports.forEach((report) => {
      const filename = String(report.filename || report.file_url || '').split('?')[0].split('#')[0];
      const match = filename.match(/\.([^.\/]+)$/);
      if (match?.[1]) types.add(match[1].toLowerCase());
    });
    return Array.from(types).sort();
  }, [generatedReports]);

  const paginatedGeneratedReports = filteredGeneratedReports.slice(
    reportPage * reportRowsPerPage,
    reportPage * reportRowsPerPage + reportRowsPerPage
  );

  const getDefectBuildingName = (id) => {
    const match = buildings.find((item) => String(item.id) === String(id));
    return match?.name || (id ? `Building #${id}` : '');
  };

  const getDefectScopeLabel = () => {
    if (reportListBuildingId === 'all') return 'All Buildings';
    if (reportListBuildingId === 'combined') return 'Combined Project Reports';
    return getDefectBuildingName(reportListBuildingId) || 'Select a building';
  };

  const buildingScopedDefects = React.useMemo(() => {
    if (!reportListBuildingId || reportListBuildingId === 'all' || reportListBuildingId === 'combined') {
      return realDefects;
    }
    return realDefects.filter((defect) => String(defect.buildingId || '') === String(reportListBuildingId));
  }, [realDefects, reportListBuildingId]);

  const defectTypeOptions = React.useMemo(() => {
    return [...new Set(buildingScopedDefects.map((defect) => defect.defectType).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
  }, [buildingScopedDefects]);

  const defectSeverityOptions = React.useMemo(() => {
    return [...new Set(buildingScopedDefects.map((defect) => defect.severity).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
  }, [buildingScopedDefects]);

  const filteredDefects = React.useMemo(() => {
    const q = defectSearch.trim().toLowerCase();
    return buildingScopedDefects.filter((defect) => {
      if (defectSeverityFilter !== 'all' && String(defect.severity || '') !== String(defectSeverityFilter)) {
        return false;
      }
      if (defectTypeFilter !== 'all' && String(defect.defectType || '') !== String(defectTypeFilter)) {
        return false;
      }
      if (!q) return true;
      return [
        defect.imageNo,
        defect.imageName,
        defect.elevation,
        defect.floor,
        defect.defectType,
        defect.recommendation,
        defect.severity,
        getDefectBuildingName(defect.buildingId),
      ].some((value) => String(value || '').toLowerCase().includes(q));
    });
  }, [buildingScopedDefects, defectSearch, defectSeverityFilter, defectTypeFilter, buildings]);

  const paginatedDefects = filteredDefects.slice(
    defectPage * defectRowsPerPage,
    defectPage * defectRowsPerPage + defectRowsPerPage
  );

  useEffect(() => {
    setDefectPage(0);
  }, [reportListBuildingId, defectSearch, defectSeverityFilter, defectTypeFilter, defectRowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredDefects.length / defectRowsPerPage) - 1);
    if (defectPage > maxPage) {
      setDefectPage(maxPage);
    }
  }, [filteredDefects.length, defectRowsPerPage, defectPage]);

  const handleReportPageChange = (event, nextPage) => {
    setReportPage(nextPage);
  };

  const handleReportRowsPerPageChange = (event) => {
    setReportRowsPerPage(parseInt(event.target.value, 10));
    setReportPage(0);
  };

  const handleDefectPageChange = (event, nextPage) => {
    setDefectPage(nextPage);
  };

  const handleDefectRowsPerPageChange = (event) => {
    setDefectRowsPerPage(parseInt(event.target.value, 10));
    setDefectPage(0);
  };

  useEffect(() => {
    if (!reportIndicatorVisible) {
      setReportElapsedSeconds(0);
      return undefined;
    }

    setReportElapsedSeconds(0);
    setReportProgress((current) => Math.max(8, current || 8));
    const timer = window.setInterval(() => {
        setReportElapsedSeconds((seconds) => {
        const next = seconds + 1;
        if (!reportReadyToOpen) {
          const estimated = Math.max(8, reportExpectedSeconds || 12);
          const nextProgress = Math.min(94, Math.max(8, Math.round((next / estimated) * 100)));
          setReportProgress(nextProgress);
        }
        return next;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [reportIndicatorVisible, reportExpectedSeconds, reportReadyToOpen]);

  useEffect(() => {
    if (!reportReadyToOpen || reportProgress < 100 || !reportFileUrl) {
      return undefined;
    }

    if (reportOpenTimerRef.current) {
      clearTimeout(reportOpenTimerRef.current);
      reportOpenTimerRef.current = null;
    }

    reportOpenTimerRef.current = window.setTimeout(() => {
      openReportFile(reportFileUrl);
      scheduleReportIndicatorHide(2500);
      if (isMountedRef.current) {
        if (closeReportTimerRef.current) clearTimeout(closeReportTimerRef.current);
        closeReportTimerRef.current = window.setTimeout(() => {
          if (isMountedRef.current) {
            setReportJob(null);
          }
        }, 3000);
        setReportReadyToOpen(false);
      }
    }, 120);

    return () => {
      if (reportOpenTimerRef.current) {
        clearTimeout(reportOpenTimerRef.current);
        reportOpenTimerRef.current = null;
      }
    };
  }, [reportReadyToOpen, reportProgress, reportFileUrl]);

  useEffect(() => {
    const status = reportJob?.status || (reportReadyToOpen ? 'completed' : reportIndicatorVisible ? 'running' : null);
    if (!status || !reportNotificationIdRef.current) return;

    if (status === 'running') {
      pushReportNotification({
        title: 'Generating report',
        message: `Generating report... ${Math.min(100, Math.max(0, Math.round(reportProgress)))}%`,
        status: 'running',
        progress: reportProgress,
        ...getReportNotificationDetails(),
      });
      return;
    }

    if (status === 'completed') {
      pushReportNotification({
        title: 'Report ready',
        message: `Report ready for ${getProjectDisplayName() || 'this project'}${getBuildingDisplayName() ? ` in ${getBuildingDisplayName()}` : ''}.`,
        status: 'completed',
        progress: 100,
        ...getReportNotificationDetails(reportFileUrl),
      });
      return;
    }

    if (status === 'failed') {
      pushReportNotification({
        title: 'Report failed',
        message: reportJob?.message || 'Failed to generate report',
        status: 'failed',
        progress: 0,
        read: false,
        ...getReportNotificationDetails(),
      });
    }
  }, [reportJob?.status, reportJob?.message, reportIndicatorVisible, reportReadyToOpen, reportProgress]);

  useEffect(() => {
    const jobId = reportJob?.job_id;
    const jobStatus = reportJob?.status;
    if (!jobId || (jobStatus !== 'queued' && jobStatus !== 'running')) {
      if (reportJobPollTimerRef.current) {
        clearInterval(reportJobPollTimerRef.current);
        reportJobPollTimerRef.current = null;
      }
      reportJobPollErrorCountRef.current = 0;
      return undefined;
    }

    let cancelled = false;

    const syncReportJob = async () => {
      try {
        const job = await apiService.getReportJob(jobId);
        reportJobPollErrorCountRef.current = 0;
        if (cancelled || !isMountedRef.current || !job) return;

        const nextStatus = job.status || jobStatus;
        const jobFormat = String(job.out_format || reportJob?.format || reportFormat || '').toUpperCase();
        const isDocxJob = jobFormat === 'DOCX';
        const nextMessage = job.message || reportJob?.message || (isDocxJob ? 'Generating DOCX report. This can take several minutes...' : 'Generating report...');
        const nextFileUrl = job.file_url || reportFileUrl || '';
        const nextProgress =
          nextStatus === 'completed'
            ? 100
            : nextStatus === 'failed'
              ? 0
              : nextStatus === 'running'
                ? Math.min(95, Math.max(20, reportProgress || 20))
                : Math.min(15, Math.max(8, reportProgress || 8));

        setReportJob((current) => ({
          ...(current || {}),
          ...job,
          job_id: job.job_id || jobId,
          status: nextStatus,
          message: nextMessage,
          format: jobFormat || current?.format || reportFormat,
          progress: nextProgress,
          file_url: nextFileUrl || (current?.file_url || ''),
        }));

        if (nextStatus === 'completed' && nextFileUrl) {
          setGenerating(false);
          setReportFileUrl(nextFileUrl);
          setReportReadyToOpen(true);
          setReportIndicatorVisible(true);
          setReportProgress(100);
          loadGeneratedReports();
        } else if (nextStatus === 'failed') {
          setGenerating(false);
          setReportReadyToOpen(false);
          setReportIndicatorVisible(true);
          setReportProgress(0);
          setReportFileUrl('');
          scheduleReportIndicatorHide(6000);
        } else {
          setGenerating(true);
          setReportIndicatorVisible(true);
          setReportProgress((current) => Math.max(current, nextProgress));
        }

        if (reportNotificationIdRef.current) {
          pushReportNotification({
            title: nextStatus === 'completed' ? 'Report ready' : nextStatus === 'failed' ? 'Report failed' : 'Generating report',
            message: nextMessage,
            status: nextStatus,
            progress: nextProgress,
            read: nextStatus === 'failed' ? false : undefined,
            jobId,
            ...getReportNotificationDetails(nextFileUrl),
          });
        }

        if (nextStatus === 'completed' || nextStatus === 'failed') {
          if (reportJobPollTimerRef.current) {
            clearInterval(reportJobPollTimerRef.current);
            reportJobPollTimerRef.current = null;
          }
        }
      } catch (error) {
        console.error('Failed to poll report job:', error);
        const nextErrorCount = (reportJobPollErrorCountRef.current || 0) + 1;
        reportJobPollErrorCountRef.current = nextErrorCount;
        const isMissingJob = Number(error?.status) === 404;
        const isDocxJob = String(reportJob?.out_format || reportJob?.format || reportFormat || '').toUpperCase() === 'DOCX';
        const maxPollErrors = isDocxJob ? 24 : 3;
        const shouldFail = isMissingJob || nextErrorCount >= maxPollErrors;
        if (shouldFail && isMountedRef.current) {
          const failureMessage = isMissingJob
            ? 'Report job status is no longer available. Please generate the report again.'
            : 'Unable to reach the report job status endpoint. Please try generating the report again.';
          setReportJob((current) => ({
            ...(current || {}),
            status: 'failed',
            message: failureMessage,
            error: error?.message || failureMessage,
          }));
          setGenerating(false);
          setReportReadyToOpen(false);
          setReportIndicatorVisible(true);
          setReportProgress(0);
          setReportFileUrl('');
          scheduleReportIndicatorHide(6000);
          pushReportNotification({
            title: 'Report failed',
            message: failureMessage,
            status: 'failed',
            progress: 0,
            format: String(reportJob?.format || reportFormat || '').toUpperCase(),
            projectName: getProjectDisplayName(),
            buildingName: getBuildingDisplayName(),
            filename: '',
            read: false,
          });
          if (reportJobPollTimerRef.current) {
            clearInterval(reportJobPollTimerRef.current);
            reportJobPollTimerRef.current = null;
          }
        } else if (isMountedRef.current) {
          setGenerating(true);
          setReportIndicatorVisible(true);
          setReportJob((current) => ({
            ...(current || {}),
            status: current?.status || 'running',
            message: isDocxJob
              ? 'Generating DOCX report. This can take several minutes...'
              : (current?.message || 'Generating report...'),
          }));
        }
      }
    };

    syncReportJob();
    reportJobPollTimerRef.current = window.setInterval(syncReportJob, 2500);

    return () => {
      cancelled = true;
      if (reportJobPollTimerRef.current) {
        clearInterval(reportJobPollTimerRef.current);
        reportJobPollTimerRef.current = null;
      }
    };
  }, [reportJob?.job_id, reportJob?.status]);

  const loadKeyFindingsFromAPI = async (bid) => {
    if (!bid) return;
    try {
      const resp = await apiService.listKeyFindings(bid, imageType || 'drone');
      const items = Array.isArray(resp?.items) ? resp.items : [];
      const ids = items.map((it) => String(it?.image_id || '')).filter(Boolean);
      setKeyFindingImageIds(ids);
      try {
        localStorage.setItem(`key_findings_${imageType || 'drone'}_building_${bid}`, JSON.stringify(ids));
      } catch {}
      setKeyFindingImageId((prev) => {
        if (prev && ids.includes(String(prev))) return prev;
        const next = ids[0] || '';
        try {
          localStorage.setItem(`key_finding_building_${bid}`, next);
        } catch {}
        return next;
      });
    } catch (e) {
      console.error('Failed to load key findings from API:', e);
    }
  };

  const loadElevationCoverFromAPI = async (bid) => {
    if (!bid || bid === 'combined') {
      setElevationCoverMap({});
      return;
    }
    try {
      const resp = await apiService.getElevationCover(bid, imageType || 'drone');
      setElevationCoverMap(resp?.cover_by_elevation || {});
    } catch (e) {
      console.error('Failed to load elevation cover map from API:', e);
      setElevationCoverMap({});
    }
  };

  // Function to load project-specific data from API
  const loadProjectDataFromAPI = async (projectId) => {
    try {
      setLoading(true);
      
      // Get project images and defects
      const response = await apiService.getProjectImages(projectId);
      
      // Extract folder structure from response
      const folderStructure = response.folder_structure || {};
      
      const orderedImages = [];
      Object.keys(folderStructure)
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
        .forEach((elevation) => {
          const elevationImages = [...(folderStructure[elevation] || [])];
          elevationImages.sort((a, b) =>
            String(a?.filename || '').localeCompare(String(b?.filename || ''), undefined, { numeric: true, sensitivity: 'base' })
          );
          orderedImages.push(...elevationImages);
        });

      const imageNoById = new Map();
      orderedImages.forEach((img, idx) => {
        if (img?.id != null) imageNoById.set(String(img.id), idx + 1);
      });
      
      // Calculate statistics from API data
      const totalImages = orderedImages.length;
      const annotatedImages = orderedImages.filter(img => img.defects && img.defects.length > 0).length;
      
      // Get unique elevations
      const elevations = Object.keys(folderStructure);
      const totalElevations = elevations.length;
      
      // Count total defects
      const totalDefects = orderedImages.reduce((total, img) => {
        return total + (img.defects ? img.defects.length : 0);
      }, 0);
      
      // Transform defects for the component
      const defects = [];
      orderedImages.forEach(img => {
        if (img.defects && img.defects.length > 0) {
          img.defects.forEach(defect => {
            defects.push({
              id: defect.id,
              imageId: img.id,
              imageNo: imageNoById.get(String(img.id)) || null,
              imageName: img.filename,
              imageUrl: img.file_url || img.url || '',
              buildingId: img.building_id || img.buildingId || '',
              elevation: img.elevation_name,
              floor: img.floor_name || '',
              imageType: img.image_type || 'drone',
              defectType: defect.defect_type,
              severity: defect.severity,
              recommendation: formatRecommendation(defect.recommendation),
              x: defect.x,
              y: defect.y,
              width: defect.width,
              height: defect.height,
              createdAt: defect.created_at
            });
          });
        }
      });
      
      setRealDefects(defects);
      setProjectStats({
        totalImages,
        annotatedImages,
        totalElevations: elevations.length,
        levelsPerElevation: elevations.length > 0 ? '1-5' : '0' // Simplified for now
      });
      
    } catch (error) {
      console.error('Error loading project data from API:', error);
      // Fallback to localStorage if API fails
      loadDataFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  // Load buildings for selection
  useEffect(() => {
    const loadBuildings = async () => {
      if (!projectId) return;
      try {
        const list = await apiService.getProjectBuildings(projectId);
        setBuildings(list || []);
        if (buildingId) {
          setSelectedBuildingId(String(buildingId));
          setReportListBuildingId(String(buildingId));
        } else if (list && list.length === 1) {
          setSelectedBuildingId(String(list[0].id));
          setReportListBuildingId('all');
        } else if (list && list.length > 0) {
          setReportListBuildingId((current) => current || 'all');
        }
      } catch (e) {
        console.error('Error loading buildings for report:', e);
      }
    };
    loadBuildings();
  }, [projectId, buildingId]);

  // Preselect building when buildingId changes
  useEffect(() => {
    if (buildingId) {
      setSelectedBuildingId(String(buildingId));
      setReportListBuildingId(String(buildingId));
    }
  }, [buildingId]);

  useEffect(() => {
    if (!selectedBuildingId) return;
    const stored = loadStoredKeyFinding(selectedBuildingId);
    if (stored) {
      setKeyFindingImageId(stored);
      return;
    }
    if (String(buildingId || '') !== String(selectedBuildingId || '')) {
      const fallback = loadStoredKeyFinding(buildingId);
      setKeyFindingImageId(fallback);
    }
  }, [selectedBuildingId, buildingId]);

  // Function to load defects and statistics from localStorage
  const loadDataFromLocalStorage = () => {
    const defects = [];
    const imageKeys = new Set();
    const elevations = new Set();
    const levels = new Set();
    let annotatedCount = 0;
    const imageNoByKey = new Map();
    let nextImageNo = 1;

    // Scan all localStorage keys for annotation data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('_data')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.defects && data.defects.length > 0) {
            // Extract image info from key (format: annotation_elevation_level_imagename_data)
            const keyParts = key.replace('_data', '').split('_');
            if (keyParts.length >= 4) {
              const elevation = keyParts[1];
              const level = keyParts[2];
              const imageName = keyParts.slice(3).join('_');
              const baseKey = key.replace('_data', '');
              
              elevations.add(elevation);
              levels.add(`${elevation}_${level}`);
              imageKeys.add(baseKey);
              annotatedCount++;
              if (!imageNoByKey.has(baseKey)) {
                imageNoByKey.set(baseKey, nextImageNo);
                nextImageNo += 1;
              }
              
              // Create defect entries
              data.defects.forEach((defect, index) => {
                const defectType =
                  typeof defect === 'object'
                    ? (defect.type || defect.defectType || defect.description || defect.name || 'Unknown Defect')
                    : defect;
                const rawCount =
                  typeof defect === 'object'
                    ? (defect.count ?? defect.qty ?? defect.quantity ?? defect.occurrences ?? 1)
                    : 1;
                const parsedCount = Number(rawCount);
                const count = Number.isFinite(parsedCount) && parsedCount > 0 ? Math.floor(parsedCount) : 1;
                for (let c = 0; c < count; c++) {
                  defects.push({
                    id: `${key}_${index}_${c}`,
                    defectType,
                    recommendation: formatRecommendation(data.recommendation),
                    severity: 'Medium',
                    description: `Defect found in ${imageName}: ${defectType}`,
                    imageKey: baseKey,
                    imageNo: imageNoByKey.get(baseKey) || null,
                  });
                }
              });
            }
          }
        } catch (error) {
          console.error('Error parsing localStorage data:', error);
        }
      } else if (key && key.startsWith('annotation_') && !key.includes('_data')) {
        // Count total images (annotation keys without _data suffix)
        imageKeys.add(key);
      }
    }

    setRealDefects(defects);
    setProjectStats({
      totalImages: imageKeys.size,
      annotatedImages: annotatedCount,
      totalElevations: elevations.size,
      levelsPerElevation: elevations.size > 0 ? `${Math.min(...Array.from(levels).map(l => l.split('_').length - 1))}-${Math.max(...Array.from(levels).map(l => l.split('_').length - 1))}` : '0'
    });
  };

  useEffect(() => {
    if (projectId) {
      // Load data from API for specific project
      loadProjectDataFromAPI(projectId);
      if (buildingId) {
        loadKeyFindingsFromAPI(buildingId);
        loadElevationCoverFromAPI(buildingId);
      }
    } else {
      // Load data from localStorage for general reports
      loadDataFromLocalStorage();
    }
  }, [projectId, buildingId, imageType]);

  useEffect(() => {
    if (!selectedBuildingId || selectedBuildingId === 'combined') {
      setElevationCoverMap({});
      return;
    }
    loadElevationCoverFromAPI(selectedBuildingId);
  }, [selectedBuildingId, imageType]);

  useEffect(() => {
    loadGeneratedReports();
    setReportPage(0);
  }, [reportListBuildingId, projectId, buildingId, buildings]);

  useEffect(() => {
    setReportPage(0);
  }, [reportListSearch, reportListBuildingId, reportListFileType, reportRowsPerPage]);

  useEffect(() => {
    const availableKeys = new Set(filteredGeneratedReports.map(getReportSelectionKey));
    setSelectedReportKeys((current) => current.filter((key) => availableKeys.has(key)));
  }, [filteredGeneratedReports]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredGeneratedReports.length / reportRowsPerPage) - 1);
    if (reportPage > maxPage) {
      setReportPage(maxPage);
    }
  }, [filteredGeneratedReports.length, reportRowsPerPage, reportPage]);

  // Convert projectStats state to array format for rendering
  const statsArray = [
    { title: 'Total Images', value: projectStats.totalImages.toString(), className: 'total' },
    { title: 'Annotated Images', value: projectStats.annotatedImages.toString(), className: 'completed' },
    { title: 'Total Elevations', value: projectStats.totalElevations.toString(), className: 'total' },
    { title: 'Levels per Elevation', value: projectStats.levelsPerElevation, className: 'pending' }
  ];

  // Add refresh function to reload data
  const handleRefresh = () => {
    if (projectId) {
      loadProjectDataFromAPI(projectId);
    } else {
      loadDataFromLocalStorage();
    }
    if (buildingId) {
      loadKeyFindingsFromAPI(buildingId);
    }
    refreshReportQueue();
    loadGeneratedReports();
  };

  const handleToggleKeyFinding = async (imageId) => {
    if (!buildingId || !imageId) return;
    const sid = String(imageId);
    const alreadySelected = keyFindingImageIds.includes(sid);
    try {
      if (alreadySelected) {
        await apiService.removeKeyFinding(buildingId, sid, imageType || 'drone');
      } else {
        await apiService.addKeyFinding(buildingId, sid, imageType || 'drone');
      }
    } catch (e) {
      console.error('Failed to update key finding:', e);
      alert(e?.message || 'Failed to update key finding');
      return;
    }

    const nextIds = alreadySelected
      ? keyFindingImageIds.filter((x) => x !== sid)
      : [...keyFindingImageIds, sid];
    setKeyFindingImageIds(nextIds);
    try {
      localStorage.setItem(`key_findings_${imageType || 'drone'}_building_${buildingId}`, JSON.stringify(nextIds));
    } catch {}
    if (!alreadySelected) {
      setKeyFindingImageId(sid);
      try {
        localStorage.setItem(`key_finding_building_${buildingId}`, sid);
      } catch {}
    } else if (String(keyFindingImageId || '') === sid) {
      const nextSingle = nextIds[0] || '';
      setKeyFindingImageId(nextSingle);
      try {
        localStorage.setItem(`key_finding_building_${buildingId}`, nextSingle);
      } catch {}
    }
  };

  const openReportFile = (fileUrl) => {
    if (typeof fileUrl !== 'string') return;
    const normalizedPath = fileUrl.trim();
    if (!normalizedPath || normalizedPath.toLowerCase() === 'null' || !normalizedPath.startsWith('/uploads/')) {
      console.error('Invalid report file URL returned by the backend:', fileUrl);
      if (isMountedRef.current) {
        setReportJob({
          open: true,
          status: 'failed',
          message: 'The report finished, but the download URL was invalid.',
          error: 'Invalid report file URL',
        });
      }
      return;
    }
    const absoluteUrl = new URL(normalizedPath, apiService.baseURL).toString();
    window.open(absoluteUrl, '_blank');
  };

  const startReportGenerationUi = ({ closeDialog = true, force = false } = {}) => {
    if (!selectedBuildingId) return false;
    const normalizedReportFormat = String(reportFormat || 'PDF').toUpperCase();
    if (selectedBuildingId === 'combined' && normalizedReportFormat !== 'PDF') return false;
    if (reportUiStartedRef.current && !force) {
      if (closeDialog && isMountedRef.current) {
        flushSync(() => setShowGenerateDialog(false));
      }
      return true;
    }

    const estimatedSeconds = getEstimatedReportSeconds();
    reportUiStartedRef.current = true;
    if (isMountedRef.current) {
      flushSync(() => {
        if (closeDialog) {
          setShowGenerateDialog(false);
        }
        setGenerating(true);
        setReportIndicatorVisible(true);
        setReportElapsedSeconds(0);
        setReportProgress(8);
        setReportExpectedSeconds(estimatedSeconds);
        setReportReadyToOpen(false);
        setReportFileUrl('');
        reportNotificationIdRef.current = null;
        reportStartedAtRef.current = Date.now();
        clearReportIndicatorHideTimer();
        if (closeReportTimerRef.current) {
          clearTimeout(closeReportTimerRef.current);
          closeReportTimerRef.current = null;
        }
        setReportJob({
          open: true,
          status: 'running',
          message: 'Generating report...',
          format: reportFormat,
        });
      });
    }
    return true;
  };

  const handleGenerateReport = async () => {
    if (!selectedBuildingId) {
      alert('Please select a building to generate the report.');
      return;
    }
    const normalizedReportFormat = String(reportFormat || 'PDF').toUpperCase();
    if (selectedBuildingId === 'combined' && normalizedReportFormat !== 'PDF') {
      alert('Combined project reports are available as PDF only. Select a single building to generate DOCX or XLSX.');
      return;
    }

    if (isMountedRef.current) {
      flushSync(() => setShowGenerateDialog(false));
    }
    await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));

    const uiStarted = startReportGenerationUi({ closeDialog: false, force: true });
    if (!uiStarted) return;
    let backgroundJobStarted = false;
    const runningMessage = normalizedReportFormat === 'DOCX'
      ? 'Generating DOCX report. This can take several minutes...'
      : 'Generating report...';

    try {
      pushReportNotification({
        title: 'Generating report',
        message: runningMessage,
        status: 'running',
        progress: 8,
        projectName: getProjectDisplayName(),
        buildingName: getBuildingDisplayName(),
        filename: '',
        read: false,
      });

      await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));

      let response;
      const payload = {
        image_type: imageType || 'drone',
      };
      if (elevationCoverMap && Object.keys(elevationCoverMap).length > 0) {
        payload.cover_by_elevation = elevationCoverMap;
        payload.elevation_cover_by_elevation = elevationCoverMap;
      }
      const selectedSingleKey =
        keyFindingImageId ||
        loadStoredKeyFinding(selectedBuildingId) ||
        loadStoredKeyFinding(buildingId);
      if (selectedSingleKey) {
        payload.key_finding_image_id = selectedSingleKey;
      }
      const selectedDroneIds = Array.from(
        new Set([
          ...keyFindingImageIds,
          ...loadStoredDroneKeyFindings(selectedBuildingId),
          ...loadStoredDroneKeyFindings(buildingId),
        ])
      );
      if (selectedDroneIds.length > 0) {
        payload.key_findings_image_ids = selectedDroneIds;
      }
      if (selectedBuildingId === 'combined') {
        response = await apiService.generateProjectReport(projectId, reportFormat, payload, true);
      } else {
        try {
          response = await apiService.generateBuildingReport(Number(selectedBuildingId), reportFormat, payload, true);
        } catch (err) {
          // Fallback: retry without payload for servers that don't accept JSON body
          response = await apiService.generateBuildingReport(Number(selectedBuildingId), reportFormat, null, true);
        }
      }

      const jobId = response?.job_id || null;
      if (jobId) {
        backgroundJobStarted = true;
        const nextStatus = response?.status || 'queued';
        const initialProgress = nextStatus === 'running' ? 20 : 8;
        if (isMountedRef.current) {
          setReportJob((current) => ({
            ...(current || {}),
            open: true,
            job_id: jobId,
            status: nextStatus,
            message: response?.message || (normalizedReportFormat === 'DOCX' ? 'Queued for DOCX generation. This can take several minutes.' : 'Queued for background generation.'),
            format: reportFormat,
            out_format: normalizedReportFormat,
            timeout_seconds: response?.timeout_seconds,
            file_url: '',
            progress: initialProgress,
          }));
          setGenerating(true);
          setReportIndicatorVisible(true);
          setReportReadyToOpen(false);
          setReportFileUrl('');
          setReportProgress(initialProgress);
        }
        pushReportNotification({
          title: nextStatus === 'running' ? 'Generating report' : 'Report queued',
          message: response?.message || (normalizedReportFormat === 'DOCX' ? 'Queued for DOCX generation. This can take several minutes.' : 'Queued for background generation.'),
          status: nextStatus,
          progress: initialProgress,
          format: String(reportFormat || '').toUpperCase(),
          projectName: getProjectDisplayName(),
          buildingName: getBuildingDisplayName(),
          filename: '',
          reportName: '',
          file_url: '',
          jobId,
          read: false,
        });
        return;
      }

      const fileUrl = response?.file_url || null;
      if (fileUrl) {
        if (isMountedRef.current) {
          setReportJob((current) => ({
            ...(current || {}),
            open: true,
            status: 'completed',
            message: 'Report is ready. Opening the file now.',
            file_url: fileUrl,
          }));
        }
        pushReportNotification({
          title: 'Report ready',
          message: `Report ready for ${getProjectDisplayName() || 'this project'}${getBuildingDisplayName() ? ` in ${getBuildingDisplayName()}` : ''}.`,
          status: 'completed',
          progress: 100,
          format: String(reportFormat || '').toUpperCase(),
          projectName: getProjectDisplayName(),
          buildingName: getBuildingDisplayName(),
          filename: getReportFilename(fileUrl),
          reportName: getReportName(fileUrl),
          file_url: fileUrl,
          read: false,
        });
        setReportFileUrl(fileUrl);
        setReportReadyToOpen(true);
        setReportProgress(100);
        loadGeneratedReports();
      }
    } catch (error) {
      console.error('Error generating report:', error);
        if (isMountedRef.current) {
          setReportJob({
            open: true,
            status: 'failed',
            message: error.message || 'Failed to generate report',
            error: error.message || 'Failed to generate report',
          });
          pushReportNotification({
            title: 'Report failed',
            message: error.message || 'Failed to generate report',
            status: 'failed',
            progress: 0,
            format: String(reportFormat || '').toUpperCase(),
            projectName: getProjectDisplayName(),
            buildingName: getBuildingDisplayName(),
            filename: '',
            read: false,
          });
          setReportProgress(0);
          setGenerating(false);
          setReportIndicatorVisible(true);
          setReportReadyToOpen(false);
          setReportFileUrl('');
        }
        scheduleReportIndicatorHide(6000);
    } finally {
      reportUiStartedRef.current = false;
      if (isMountedRef.current) {
        if (!backgroundJobStarted) {
          setGenerating(false);
        }
        setLoading(false);
      }
      if (closeReportTimerRef.current) {
        clearTimeout(closeReportTimerRef.current);
        closeReportTimerRef.current = null;
      }
    }
  };

  const handleDownload = (defectId) => {
    console.log('Downloading defect report:', defectId);
    // TODO: Implement defect report download functionality
  };

  const handlePreview = (def) => {
    setPreviewItem(def);
    setPreviewOpen(true);
  };

  const reportInProgress = reportJob?.status === 'queued' || reportJob?.status === 'running';
  const localReportActive = Boolean(generating || reportInProgress);
  const reportPanelVisible = Boolean(reportIndicatorVisible || reportReadyToOpen);
  const reportButtonDisabled = generating || reportInProgress;
  const dialogGenerateDisabled = generating || !selectedBuildingId;
  const reportStatusComplete = reportJob?.status === 'completed' || reportProgress >= 100;
  const reportStatusLabel = reportJob?.status === 'failed'
    ? 'Failed'
    : reportStatusComplete
      ? 'Complete'
      : 'Generating';
  const activeReportJobs = Array.isArray(reportJobStatusSummary?.active_jobs)
    ? reportJobStatusSummary.active_jobs
    : reportJobStatusSummary?.active_job
      ? [reportJobStatusSummary.active_job]
      : [];
  const activeReportJobCount = Number(reportJobStatusSummary?.running || 0) + Number(reportJobStatusSummary?.queued || 0);

  return (
    <ReportsContainer>
      <HeaderSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBack && (
            <IconButton onClick={onBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box>
            <Typography variant="h4" gutterBottom>
              {projectName ? `Reports - ${projectName}` : 'Reports'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {projectId 
                ? `Generate and manage inspection reports for project ID: ${projectId}`
                : 'Generate and manage inspection reports for your projects'
              }
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<PendingActionsIcon />}
            onClick={() => setShowReportStatusDialog(true)}
          >
            Report Status{activeReportJobCount > 0 ? ` (${activeReportJobCount})` : ''}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowGenerateDialog(true)}
            disabled={reportButtonDisabled}
          >
            {reportButtonDisabled ? 'Generating Report' : 'Generate New Report'}
          </Button>
        </Box>
      </HeaderSection>

      <Portal>
        {reportPanelVisible && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 3000,
              pointerEvents: 'none',
            }}
          >
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              right: 24,
              bottom: 24,
              width: 'min(360px, calc(100vw - 32px))',
              p: 1.5,
              zIndex: 3000,
              borderRadius: 2,
              backgroundColor: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              pointerEvents: 'auto',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75, color: 'text.primary' }}>
              {reportJob?.status === 'failed' ? 'Report failed' : reportReadyToOpen ? 'Report generated' : 'Loading...'}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, reportProgress))}
              sx={{
                height: 8,
                borderRadius: 999,
                backgroundColor: 'rgba(0,0,0,0.14)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                },
              }}
              color={reportJob?.status === 'failed' ? 'error' : reportStatusComplete ? 'success' : 'primary'}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                {Math.min(100, Math.max(0, Math.round(reportProgress)))}%
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: reportJob?.status === 'failed' ? 'error.main' : reportStatusComplete ? 'success.main' : 'text.secondary',
                }}
              >
                {reportStatusLabel}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color={reportJob?.status === 'failed' ? 'error.main' : 'text.secondary'}
              sx={{ display: 'block', mt: 0.5 }}
            >
              {reportJob?.status === 'failed'
                ? (reportJob?.message || 'Failed to generate report')
                : `${reportElapsedSeconds}s elapsed`}
            </Typography>
          </Paper>
          </Box>
        )}
      </Portal>

      {/* Project Statistics Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statsArray.map((stat, index) => (
            <Grid item xs={12} md={3} key={index}>
              <StatsCard className={stat.className}>
                <CardContent>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Generated Reports Cleanup/Retention Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6">Generated Reports</Typography>
            <Typography variant="body2" color="text.secondary">
              {getReportListScope().label} · {filteredGeneratedReports.length} of {generatedReports.length} reports
              {cleanupRules ? ` - Cleanup rule: unkept reports older than ${cleanupRules.retention_days} days or outside latest ${cleanupRules.keep_latest_unretained} unkept reports.` : ''}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadGeneratedReports}
            disabled={reportListLoading || deletingReports}
          >
            Refresh Reports
          </Button>
          {selectedReportKeys.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={deletingReports ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
              onClick={handleDeleteSelectedReports}
              disabled={deletingReports}
            >
              Delete Selected ({selectedReportKeys.length})
            </Button>
          )}
        </Box>

        {reportListError && <Alert severity="error" sx={{ mb: 2 }}>{reportListError}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '260px 180px minmax(260px, 1fr)' },
            gap: 2,
            mb: 2,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel>Building</InputLabel>
            <Select
              value={reportListBuildingId}
              label="Building"
              onChange={(e) => handleSharedBuildingFilterChange(e.target.value)}
            >
              <MenuItem value="all">All Buildings</MenuItem>
              <MenuItem value="combined">Combined Project Reports</MenuItem>
              {buildings.map((building) => (
                <MenuItem key={building.id} value={String(building.id)}>
                  {building.name || `Building #${building.id}`}
                </MenuItem>
              ))}
              {!['all', 'combined'].includes(String(reportListBuildingId)) &&
                reportListBuildingId &&
                !buildings.some((building) => String(building.id) === String(reportListBuildingId)) && (
                  <MenuItem value={reportListBuildingId}>{`Building #${reportListBuildingId}`}</MenuItem>
                )}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>File Type</InputLabel>
            <Select
              value={reportListFileType}
              label="File Type"
              onChange={(e) => setReportListFileType(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              {reportFileTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>{type.toUpperCase()}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            placeholder="Filename, building, date, cleanup status"
            value={reportListSearch}
            onChange={(e) => setReportListSearch(e.target.value)}
            fullWidth
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    disabled={reportListLoading || filteredGeneratedReports.length === 0}
                    checked={filteredGeneratedReports.length > 0 && filteredGeneratedReports.every((report) => selectedReportKeys.includes(getReportSelectionKey(report)))}
                    indeterminate={filteredGeneratedReports.some((report) => selectedReportKeys.includes(getReportSelectionKey(report))) && !filteredGeneratedReports.every((report) => selectedReportKeys.includes(getReportSelectionKey(report)))}
                    onChange={(event) => handleToggleAllReports(event.target.checked)}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>REPORT FILE</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>CREATED</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>GENERATED BY</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>SIZE</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>RETENTION</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>CLEANUP STATUS</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportListLoading && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!reportListLoading && filteredGeneratedReports.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {generatedReports.length === 0 ? 'No generated reports found for this selection.' : 'No reports match this filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!reportListLoading && paginatedGeneratedReports.map((report) => {
                const scope = getReportListScope();
                const reportScopeType = report.scope_type || scope.type;
                const reportScopeId = report.scope_id || scope.id;
                const updateKey = `${reportScopeType}:${reportScopeId}:${report.filename}`;
                const updating = updatingRetentionKey === updateKey;
                const selectionKey = getReportSelectionKey(report);
                const selected = selectedReportKeys.includes(selectionKey);
                return (
                  <TableRow key={report.file_url || report.filename} hover selected={selected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected}
                        onChange={() => handleToggleReportSelection(report)}
                      />
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{report.filename}</Typography>
                          {report.scope_label && (
                            <Typography variant="caption" color="text.secondary">
                              {report.scope_label}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{formatReportDate(report.created_at)}</TableCell>
                    <TableCell>{report.generated_by_name || '—'}</TableCell>
                    <TableCell>{formatReportSize(report.size)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={report.retained ? <PushPinIcon /> : undefined}
                        label={report.retained ? 'Keep' : 'Not kept'}
                        color={report.retained ? 'success' : 'default'}
                        variant={report.retained ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      {report.retained ? (
                        <Chip size="small" label="Protected" color="success" variant="outlined" />
                      ) : report.cleanup_candidate ? (
                        <Chip
                          size="small"
                          icon={<DeleteSweepIcon />}
                          label="Cleanup candidate"
                          color="warning"
                          title={report.cleanup_reason || 'Eligible for cleanup or archiving'}
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<ArchiveIcon />}
                          label="Unmarked"
                          variant="outlined"
                          title="Unmarked reports are tracked for future cleanup based on system rules"
                        />
                      )}
                      {report.cleanup_reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {report.cleanup_reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={() => openReportFile(report.file_url)}>
                          Open
                        </Button>
                        <Button
                          size="small"
                          color={report.retained ? 'inherit' : 'success'}
                          variant={report.retained ? 'outlined' : 'contained'}
                          startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <PushPinIcon />}
                          disabled={updating}
                          onClick={() => handleToggleReportRetention(report)}
                        >
                          {report.retained ? 'Unkeep' : 'Keep'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredGeneratedReports.length}
          page={reportPage}
          onPageChange={handleReportPageChange}
          rowsPerPage={reportRowsPerPage}
          onRowsPerPageChange={handleReportRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Reports Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6">Project Defects</Typography>
            <Typography variant="body2" color="text.secondary">
              {getDefectScopeLabel()} · {filteredDefects.length} of {buildingScopedDefects.length} defects
            </Typography>
          </Box>
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) 180px 220px' },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Image, elevation, type, recommendation"
            value={defectSearch}
            onChange={(e) => setDefectSearch(e.target.value)}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Severity</InputLabel>
            <Select
              value={defectSeverityFilter}
              label="Severity"
              onChange={(e) => setDefectSeverityFilter(e.target.value)}
            >
              <MenuItem value="all">All Severity</MenuItem>
              {defectSeverityOptions.map((severity) => (
                <MenuItem key={severity} value={severity}>{severity}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Defect Type</InputLabel>
            <Select
              value={defectTypeFilter}
              label="Defect Type"
              onChange={(e) => setDefectTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              {defectTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>IMAGE NO</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>LOCATION</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>DEFECT TYPE</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>RECOMMENDATION</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>SEVERITY</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>KEY FINDING</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDefects.length > 0 ? paginatedDefects.map((defect) => (
                <TableRow key={defect.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{defect.imageNo || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{defect.elevation || '-'}</Typography>
                    {defect.floor && (
                      <Typography variant="caption" color="text.secondary">{defect.floor}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {defect.defectType}
                    </Box>
                  </TableCell>
                  <TableCell>{formatRecommendation(defect.recommendation)}</TableCell>
                  <TableCell>
                    <Chip
                      label={defect.severity}
                      color={defect.severity === 'High' ? 'error' : defect.severity === 'Medium' ? 'warning' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(defect)}
                      title={defect.description}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(defect.id)}
                      title="Download defect report"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color={keyFindingImageIds.includes(String(defect.imageId)) ? 'success' : 'primary'}
                      variant={keyFindingImageIds.includes(String(defect.imageId)) ? 'contained' : 'outlined'}
                      onClick={() => handleToggleKeyFinding(defect.imageId)}
                    >
                      {keyFindingImageIds.includes(String(defect.imageId)) ? 'Selected' : 'Select'}
                    </Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No defects found for this building or filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredDefects.length}
          page={defectPage}
          onPageChange={handleDefectPageChange}
          rowsPerPage={defectRowsPerPage}
          onRowsPerPageChange={handleDefectRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Report Status Dialog */}
      <Dialog
        open={showReportStatusDialog}
        onClose={() => setShowReportStatusDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <PendingActionsIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="span">Report Status</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Chip size="small" color="primary" label={`Running ${Number(reportJobStatusSummary?.running || 0)}`} />
              <Chip size="small" color="warning" label={`Queued ${Number(reportJobStatusSummary?.queued || 0)}`} />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {reportJobStatusError && (
            <Alert severity="error" sx={{ mb: 2 }}>{reportJobStatusError}</Alert>
          )}
          {reportJobStatusLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : activeReportJobs.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>No report jobs are running or queued.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                New PDF, DOCX, or XLSX background jobs will appear here.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>REPORT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>TARGET</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>STATUS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>ELAPSED</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>TIMEOUT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>MESSAGE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeReportJobs.map((job) => {
                    const status = String(job.status || '').toLowerCase();
                    const jobId = String(job.job_id || '');
                    return (
                      <TableRow key={jobId || `${job.job_type}-${job.target_id}-${job.position}`}>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {String(job.out_format || '').toUpperCase() || 'REPORT'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {jobId ? `${jobId.slice(0, 8)}...${jobId.slice(-6)}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Typography variant="body2">{getJobTargetLabel(job)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {job.job_type || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={getJobStatusColor(status)}
                            label={status || 'unknown'}
                          />
                          {status === 'queued' && Number(job.position || 0) > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Position {job.position}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatJobDuration(job.elapsed_seconds)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {job.timeout_seconds ? formatJobDuration(job.timeout_seconds) : '-'}
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <Typography
                            variant="body2"
                            color={status === 'failed' ? 'error.main' : 'text.primary'}
                            sx={{ whiteSpace: 'normal' }}
                          >
                            {job.error || job.message || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadReportJobStatuses()}
            disabled={reportJobStatusLoading}
          >
            Refresh
          </Button>
          <Button onClick={() => setShowReportStatusDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog
        open={showGenerateDialog}
        onClose={() => {
          if (!generating) setShowGenerateDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
            Generate New Report
          </Box>
        </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a comprehensive inspection report based on your annotated images.
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuildingId}
              onChange={(e) => {
                const nextBuildingId = e.target.value;
                setSelectedBuildingId(nextBuildingId);
                if (nextBuildingId === 'combined' && String(reportFormat || '').toUpperCase() !== 'PDF') {
                  setReportFormat('PDF');
                }
              }}
              label="Building"
            >
              <MenuItem value="combined" sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                Combine All Buildings
              </MenuItem>
              {buildings.map(b => (
                <MenuItem key={b.id} value={String(b.id)}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              label="Report Type"
            >
              <MenuItem value="full">Full Inspection Report</MenuItem>
              <MenuItem value="summary">Summary Report</MenuItem>
              <MenuItem value="defects">Defects Only</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Report Format</InputLabel>
            <Select
              value={reportFormat}
              onChange={(e) => setReportFormat(e.target.value)}
              label="Report Format"
            >
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="DOCX" disabled={selectedBuildingId === 'combined'}>
                {selectedBuildingId === 'combined' ? 'Word Document (single building only)' : 'Word Document'}
              </MenuItem>
              <MenuItem value="XLSX" disabled={selectedBuildingId === 'combined'}>
                {selectedBuildingId === 'combined' ? 'Excel Spreadsheet (single building only)' : 'Excel Spreadsheet'}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGenerateDialog(false)} disabled={generating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={dialogGenerateDisabled}
          >
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>
      {/* Defect Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Annotation Preview</DialogTitle>
        <DialogContent>
          {previewItem && previewItem.imageUrl ? (
            <Box sx={{ position: 'relative', width: '100%', border: '1px solid #e0e0e0' }}>
              <img
                alt={previewItem.imageName || 'Defect image'}
                src={
                  String(previewItem.imageUrl).startsWith('/uploads/')
                    ? `${API_BASE_URL}${previewItem.imageUrl}`
                    : previewItem.imageUrl
                }
                ref={previewImgRef}
                onLoad={(e) => {
                  const nw = e.currentTarget.naturalWidth || 0;
                  const dw = e.currentTarget.clientWidth || 0;
                  setImgDims({ naturalWidth: nw, displayedWidth: dw });
                }}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              {(() => {
                const overlays = [];
                const scale =
                  imgDims.naturalWidth && imgDims.displayedWidth
                    ? imgDims.displayedWidth / imgDims.naturalWidth
                    : 1;
                if (
                  typeof previewItem.x === 'number' &&
                  typeof previewItem.y === 'number' &&
                  typeof previewItem.width === 'number' &&
                  typeof previewItem.height === 'number'
                ) {
                  overlays.push(
                    <Box
                      key="defect-box"
                      sx={{
                        position: 'absolute',
                        left: `${previewItem.x * scale}px`,
                        top: `${previewItem.y * scale}px`,
                        width: `${previewItem.width * scale}px`,
                        height: `${previewItem.height * scale}px`,
                        border: '2px solid red',
                        boxShadow: '0 0 0 2px rgba(255,0,0,0.3) inset'
                      }}
                    />
                  );
                }
                if (previewItem.imageKey) {
                  try {
                    const raw = localStorage.getItem(previewItem.imageKey);
                    const ann = raw ? JSON.parse(raw) : [];
                    (Array.isArray(ann) ? ann : []).forEach((a, idx) => {
                      if (
                        a &&
                        a.type === 'square' &&
                        typeof a.x === 'number' &&
                        typeof a.y === 'number' &&
                        typeof a.width === 'number' &&
                        typeof a.height === 'number'
                      ) {
                        overlays.push(
                          <Box
                            key={`ann-${idx}`}
                            sx={{
                              position: 'absolute',
                              left: `${a.x * scale}px`,
                              top: `${a.y * scale}px`,
                              width: `${a.width * scale}px`,
                              height: `${a.height * scale}px`,
                              border: '2px solid rgba(0, 150, 255, 0.9)',
                              boxShadow: '0 0 0 2px rgba(0,150,255,0.3) inset'
                            }}
                          />
                        );
                      }
                    });
                  } catch {}
                }
                return overlays;
              })()}
            </Box>
          ) : (
            <Typography color="text.secondary">No image available for preview.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(reportJob && reportJob.status === 'failed')}
        autoHideDuration={5000}
        onClose={() => setReportJob(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setReportJob(null)}>
          {reportJob?.message || 'Failed to generate report'}
        </Alert>
      </Snackbar>
    </ReportsContainer>
  );
};

export default Reports;
